import { useEffect, useId, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Download, ExternalLink, Printer, X } from 'lucide-react';
import { Button } from './Button';

export interface PrintableEstimateLine {
  id: string;
  description: string;
  quantity: number;
  unit: string;
  rate: number;
  total: number;
}

export interface PrintableEstimate {
  project: string;
  customer?: string;
  title: string;
  version: string | number;
  status?: string;
  scopeOfWork?: string;
  lines: PrintableEstimateLine[];
  total: number;
}

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const decimal = new Intl.NumberFormat('en-US', { maximumFractionDigits: 3 });

/** One consistent, printer-friendly estimate layout for Staff Desk and Customer Portal. */
export function EstimatePrintDocument({ estimate }: { estimate: PrintableEstimate }) {
  return <article className="altios-estimate-print">
    <header className="altios-estimate-print__head">
      <div className="altios-estimate-print__brand">ALTIOS <span>BUILDERS</span></div>
      <div className="altios-estimate-print__head-title">
        <span>Project estimate</span>
        <h1>{estimate.title}</h1>
        <p>{estimate.project}</p>
      </div>
      <div className="altios-estimate-print__meta">
        <div><span>Version</span><strong>{estimate.version}</strong></div>
        {estimate.status ? <div><span>Status</span><strong>{estimate.status}</strong></div> : null}
        {estimate.customer ? <div><span>Prepared for</span><strong>{estimate.customer}</strong></div> : null}
      </div>
    </header>

    <section className="altios-estimate-print__section">
      <h2>Scope of work</h2>
      <p className="altios-estimate-print__scope">{estimate.scopeOfWork?.trim() || 'Scope is itemized below.'}</p>
    </section>

    <section className="altios-estimate-print__section">
      <h2>Scope items and pricing</h2>
      <table className="altios-estimate-print__table">
        <thead><tr>
          <th>Item / Description</th><th className="num">Qty</th><th>Unit</th>
          <th className="num">Unit price</th><th className="num">Amount</th>
        </tr></thead>
        <tbody>
          {estimate.lines.map((line) => <tr key={line.id}>
            <td>{line.description}</td>
            <td className="num">{decimal.format(line.quantity)}</td>
            <td>{line.unit || 'each'}</td>
            <td className="num">{currency.format(line.rate)}</td>
            <td className="num">{currency.format(line.total)}</td>
          </tr>)}
        </tbody>
        <tfoot><tr><th colSpan={4}>Estimate total</th><td className="num">{currency.format(estimate.total)}</td></tr></tfoot>
      </table>
      <p className="altios-estimate-print__note">Amounts reflect the saved estimate version. This estimate is not a fully executed construction agreement.</p>
    </section>
  </article>;
}

/** React-native document review. The print stylesheet exposes only its document content. */
export function ReviewPrintDialog({ title, children, onClose }: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}) {
  const titleId = useId();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <div className="altios-review-print-backdrop" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <div className="altios-review-print-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="altios-review-print-controls">
          <div><span>Document review</span><h2 id={titleId}>{title}</h2></div>
          <div className="altios-review-print-controls__actions">
            <Button tone="secondary" onClick={onClose} icon={<X size={16} />}>Close</Button>
            <Button tone="positive" onClick={() => window.print()} icon={<Printer size={17} />}>Print / Save PDF</Button>
          </div>
        </header>
        <div className="altios-review-print-scroll">
          <div className="altios-review-print-content">{children}</div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/** Review the original stored PDF, then open the native PDF viewer for reliable printing. */
export function PdfReviewDialog({ title, url, onClose }: {
  title: string;
  url: string;
  onClose: () => void;
}) {
  const titleId = useId();
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <div className="altios-review-print-backdrop" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <div className="altios-review-print-dialog altios-review-print-dialog--pdf" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="altios-review-print-controls">
          <div><span>Original PDF</span><h2 id={titleId}>{title}</h2></div>
          <div className="altios-review-print-controls__actions">
            <Button tone="secondary" onClick={onClose} icon={<X size={16} />}>Close</Button>
            <a className="altios-review-print-action altios-review-print-action--secondary" href={url} download><Download size={16} /> Download</a>
            <a className="altios-review-print-action altios-review-print-action--primary" href={url} target="_blank" rel="noopener noreferrer"><ExternalLink size={16} /> Open PDF to Print</a>
          </div>
        </header>
        <div className="altios-review-print-pdf">
          <iframe src={url} title={title} />
        </div>
      </div>
    </div>,
    document.body
  );
}
